import type { AgentPart, StreamEvent } from './api-types';

/**
 * Accumulates SSE events into the parts of a single streaming assistant
 * message. Mirrors the iOS `AssistantStreamAccumulator` so both clients render
 * a stream the same way.
 *
 * Ordering matters: text and tool activity interleave, and a `tool_result`
 * must attach to its `tool_call` rather than appending a second entry. Text
 * Text and thinking deltas each coalesce into a trailing part of their own
 * type so a long stream stays readable without losing event ordering.
 */
export interface StreamState {
  messageId: string | null;
  parts: AgentPart[];
  status: 'streaming' | 'complete' | 'cancelled' | 'error';
  error: string | null;
}

export function initialStreamState(): StreamState {
  return { messageId: null, parts: [], status: 'streaming', error: null };
}

export function reduceStream(state: StreamState, event: StreamEvent): StreamState {
  switch (event.event) {
    case 'message_start':
      return { ...state, messageId: event.data.messageId };

    case 'text_delta': {
      const parts = [...state.parts];
      const last = parts[parts.length - 1];
      if (last?.type === 'text') {
        parts[parts.length - 1] = { ...last, text: (last.text ?? '') + event.data.delta };
      } else {
        parts.push({ type: 'text', text: event.data.delta });
      }
      return { ...state, messageId: state.messageId ?? event.data.messageId, parts };
    }

    case 'thinking_delta': {
      const parts = [...state.parts];
      const last = parts[parts.length - 1];
      if (last?.type === 'thinking') {
        parts[parts.length - 1] = { ...last, text: (last.text ?? '') + event.data.delta };
      } else {
        parts.push({ type: 'thinking', text: event.data.delta });
      }
      return { ...state, messageId: state.messageId ?? event.data.messageId, parts };
    }

    case 'citation': {
      const citation = event.data.citation;
      const duplicate = state.parts.some((part) => (
        part.type === 'citation'
        && part.citation?.url === citation.url
        && part.citation?.citedText === citation.citedText
      ));
      if (duplicate) return state;
      return {
        ...state,
        messageId: state.messageId ?? event.data.messageId,
        parts: [...state.parts, { type: 'citation', citation }],
      };
    }

    case 'tool_call': {
      const { toolId, toolName, input, status } = event.data;
      // The server can re-emit a call as it fills in arguments; update in place.
      const index = state.parts.findIndex((part) => part.type === 'tool_call' && part.toolId === toolId);
      const call: AgentPart = {
        type: 'tool_call',
        toolId,
        toolName,
        input,
        status: (status as AgentPart['status']) ?? 'complete',
      };
      const parts = [...state.parts];
      if (index === -1) parts.push(call);
      else parts[index] = call;
      return { ...state, messageId: state.messageId ?? event.data.messageId, parts };
    }

    case 'tool_result': {
      const { toolId, toolName, success, output, error, status } = event.data;
      const result: AgentPart = {
        type: 'tool_result',
        toolId,
        toolName,
        output,
        ...(error ? { error } : {}),
        status: (status as AgentPart['status']) ?? (success ? 'complete' : 'failed'),
      };
      const index = state.parts.findIndex((part) => part.type === 'tool_result' && part.toolId === toolId);
      const parts = [...state.parts];
      if (index === -1) parts.push(result);
      else parts[index] = result;
      return { ...state, messageId: state.messageId ?? event.data.messageId, parts };
    }

    case 'message_end': {
      if (event.data.status === 'cancelled') {
        return {
          ...state,
          status: 'cancelled',
          messageId: event.data.messageId ?? state.messageId,
        };
      }
      if (event.data.status === 'error') {
        return {
          ...state,
          status: 'error',
          error: event.data.error ?? 'The message failed.',
          messageId: event.data.messageId ?? state.messageId,
        };
      }
      return { ...state, status: 'complete', messageId: event.data.messageId ?? state.messageId };
    }

    default:
      return state;
  }
}

/** The plain text of a message, for previews and copy actions. */
export function partsToText(parts: readonly AgentPart[]): string {
  return parts
    .filter((part) => part.type === 'text')
    .map((part) => part.text ?? '')
    .join('');
}
